import { useEffect, useMemo } from "react"
import { Box } from "@mui/material"
import intl from "src/internationalization/intl"
import { Accordion, Checkbox, Chip, Grid, InvestorProfileChip, Typography } from "src/lib"
import { formatCurrencyBRL, mapPerfilToProfile } from "src/lib/transformers/ClientsOverviewInfo"
import { parseMaturityProduct } from "src/lib/utils/commitmentEvent"
import { getDueInDays } from "src/lib/utils/opportunityDates"
import { CLIENT_EVENT_ACCORDION_EXPAND_DISABLED } from "src/config/featureFlags"
import { generateRecommendationProducts, mergeRankingTexts, normalizeRecommendationResponse } from "src/services/api/commitments"
import { AllocationStrategyBars } from "./AllocationStrategyBars"
import { useCashRecommendationStore } from "src/store/cashRecommendationStore"
import { getStoreKey } from "src/store/cashRecommendationStore/types"
import { useCommitmentAccordeonStore } from "src/store"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { useModalStore } from "src/store/modalStore"
import { CashOpportunityPayload, ISummaryProps } from "./types"

const DEFAULT_RANKINGS = {
    fund_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"FUNDOS\"})\nWHERE p.minimumInitialInvestment is not null and p.minimumInitialInvestment is::FLOAT\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    fixed_income_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"RENDA FIXA\"})\nWHERE p.dateDue is not null\nORDER BY p.dateDue DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    retirement_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"PREVIDENCIA\"})\nWHERE p.minimumInitialInvestment is not null and p.minimumInitialInvestment is::FLOAT\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    variable_income_products_query: "",
    crypto_products_query: "",
    coe_products_query: "",
}

/** Bloco rotulado (label em cima, valor embaixo) usado na linha do cliente. */
const MetaItem = ({
    label,
    value,
    emphasize = false,
}: {
    label: string
    value: string
    emphasize?: boolean
}) => (
    <Grid sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
            {label}
        </Typography>
        <Typography
            variant={emphasize ? "body1" : "body2"}
            sx={{ fontWeight: emphasize ? 700 : 500 }}
        >
            {value}
        </Typography>
    </Grid>
)

const shortenFinancialInstitution = (name?: string | null): string => {
    if (!name) return ""
    const upper = name.toUpperCase()
    if (upper.includes("XP")) return "XP"
    if (upper.includes("ITAU") || upper.includes("ITAÚ")) return "Itaú"
    if (upper.includes("INTER")) return "Inter"
    if (upper.includes("MERCADO PAGO")) return "Mercado Pago"
    if (upper.includes("BTG")) return "BTG"
    return name.length > 28 ? `${name.slice(0, 25)}…` : name
}

const Summary = (props: ISummaryProps) => {
    const setOpen = useModalStore((s: any) => s.setOpen)
    const setLoading = useCashRecommendationStore((s) => s.setLoading)
    const setResult = useCashRecommendationStore((s) => s.setResult)
    const setError = useCashRecommendationStore((s) => s.setError)
    const confirmedRankingByMarket = useConfirmedRankingStore((s) => s.confirmedRankingByMarket)
    const confirmedFundos = useConfirmedRankingStore((s) => s.getConfirmedRanking("FUNDOS"))
    const confirmedPrevidencia = useConfirmedRankingStore((s) => s.getConfirmedRanking("PREVIDENCIA"))
    const confirmedRendaFixa = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA FIXA"))
    const confirmedRendaVariavel = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA VARIAVEL"))
    const rankings = useMemo(() => {
        const base = {
            ...DEFAULT_RANKINGS,
            fund_products_query: confirmedFundos?.cypher_query ?? DEFAULT_RANKINGS.fund_products_query,
            fixed_income_products_query:
                confirmedRendaFixa?.cypher_query ?? DEFAULT_RANKINGS.fixed_income_products_query,
            retirement_products_query:
                confirmedPrevidencia?.cypher_query ?? DEFAULT_RANKINGS.retirement_products_query,
            variable_income_products_query:
                confirmedRendaVariavel?.cypher_query ?? DEFAULT_RANKINGS.variable_income_products_query,
        }
        return mergeRankingTexts(base, confirmedRankingByMarket)
    }, [
        confirmedFundos?.cypher_query,
        confirmedRendaFixa?.cypher_query,
        confirmedPrevidencia?.cypher_query,
        confirmedRendaVariavel?.cypher_query,
        confirmedRankingByMarket,
    ])
    const summaryType = props.summaryType ?? "commitment"
    const isOpportunity = summaryType === "opportunity"
    const isCash = props.client?.is_cash_opportunity === true
    const isOpenFinance = props.client?.source === "open_finance"
    const isDemo = props.client?.is_demo === true
    const checked = props.client.liquidityEventResolution === true

    const rawAccount = props.client?.client?.account
    const accountStr = rawAccount != null && rawAccount !== "" ? String(rawAccount) : null
    const cashEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "cash")] : undefined))
    const commitmentEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "commitment")] : undefined))
    const accordions = useCommitmentAccordeonStore((s: any) => s.accordions)

    /**
     * Simulação simples: só quando exatamente 1 produto recomendado está marcado, move o net_amount pra
     * estratégia dele. Se for vencimento e a estratégia do produto vencendo for conhecida, desconta de lá
     * (realocação); se for caixa disponível (ou a estratégia do vencimento for desconhecida), soma como
     * dinheiro novo.
     */
    const simulatedInvestment = useMemo(() => {
        if (isOpportunity || !props.accordionId) return undefined
        const activeEntry = isCash ? cashEntry : commitmentEntry
        const products = normalizeRecommendationResponse(activeEntry?.data).list_of_products
        const selectedIds = accordions.find((a: any) => a.id === props.accordionId)?.recommendations ?? []
        if (selectedIds.length !== 1) return undefined
        const product = products[Number(selectedIds[0].id)]
        const strategy = typeof product?.strategy === "string" ? product.strategy.trim() : ""
        const amount = Number(props.client?.net_amount)
        if (!strategy || !Number.isFinite(amount) || amount <= 0) return undefined
        const fromStrategy = isCash ? undefined : props.client?.produto_a_vencer?.strategy
        return { strategy, amount, fromStrategy }
    }, [isOpportunity, isCash, cashEntry, commitmentEntry, accordions, props.accordionId, props.client?.net_amount, props.client?.produto_a_vencer?.strategy])

    const getCommitmentId = () => {
        return props.client?.produto_a_vencer?.commitmentId || ""
    }

    const handleCheckboxClick = (e?: any) => {
        e?.stopPropagation?.()
        if (isDemo) return
        const commitmentId = getCommitmentId()
        const clientName = props.client?.client?.name || ""
        const accountId = props.client?.client?.account

        if (accountId === null || accountId === undefined || accountId === "") {
            console.error("Erro: accountId do cliente é obrigatório e não pode ser null, undefined ou vazio")
            return
        }

        setOpen("confirmCommitmentStatusModal", {
            commitmentId,
            currentStatus: checked,
            clientName,
            accountId,
            dueDate: props.client?.last_updated_date || "",
        })
    }

    const opportunities = props.opportunities ?? []
    const totalOpportunityValue = opportunities.reduce((acc: number, opportunity: any) => {
        return acc + (opportunity?.volumeOportunidade ?? 0)
    }, 0)

    // Evento em linguagem humana
    const maturity = parseMaturityProduct(props.client?.produto_a_vencer?.nameProduct)
    const eventTitle = isOpportunity
        ? intl("opportunities")
        : isCash
            ? intl("cashAvailable")
            : intl("maturityOf", { values: { code: maturity.code } })
    const eventSubtitle = !isOpportunity && !isCash && maturity.dateText ? maturity.dateText : null

    // Sistema de urgência (apenas para vencimentos): 🔴 ≤3d · 🟡 ≤10d · 🟢 >10d
    const urgency = (() => {
        if (isOpportunity || isCash) return null
        const dueDate = (props as any).commitment?.due_date
        if (!dueDate) return null
        const days = getDueInDays({ dueDate })
        if (days == null) return null
        const color = days <= 3 ? "accentError.main" : days <= 10 ? "secondary.main" : "accentSuccess.main"
        const label =
            days < 0
                ? intl("overdueByDaysLabel", { values: { days: Math.abs(days) } })
                : days === 0
                    ? intl("dueToday")
                    : days === 1
                        ? intl("dueTomorrow")
                        : intl("dueInDaysLabel", { values: { days } })
        return { color, label }
    })()

    const streamChip = isOpportunity ? (
        <Chip label={intl("oportunidade")} sx={{ backgroundColor: "#BD6DB6", color: "#fff" }} />
    ) : isCash ? (
        <Chip color="warning" label={intl("caixa")} />
    ) : (
        <Chip color="error" label={intl("maturity")} />
    )

    const institutionLabel = shortenFinancialInstitution(props.client?.finantialInstitution ?? "BTG")

    useEffect(() => {
        if (CLIENT_EVENT_ACCORDION_EXPAND_DISABLED || !props.open) return
        const account = accountStr
        if (account == null) return

        if (props.client?.is_cash_opportunity) {
            const payload: CashOpportunityPayload = {
                client: account,
                opportunity: "",
                event: { cliente_com_caixa: { net_worth: Number(props.client?.net_worth ?? 0) } },
                rankings,
                associated_opportunities: [],
            }
            setLoading(account, "cash")
            generateRecommendationProducts(payload)
                .then((response) => setResult(account, "cash", response))
                .catch((err: any) => setError(account, "cash", err?.message ?? "Erro ao carregar recomendações."))
            return
        }

        if (props.client?.produto_a_vencer) {
            const payload: CashOpportunityPayload = {
                client: account,
                opportunity: "",
                event: {
                    produto_a_vencer: {
                        commitmentId: props.client?.produto_a_vencer?.commitmentId ?? "",
                        nameProduct: props.client?.produto_a_vencer?.nameProduct ?? "",
                    },
                },
                rankings,
                associated_opportunities: [],
            }
            setLoading(account, "commitment")
            generateRecommendationProducts(payload)
                .then((response) => setResult(account, "commitment", response))
                .catch((err: any) => setError(account, "commitment", err?.message ?? "Erro ao carregar recomendações."))
        }
    }, [props.open, props.client?.is_cash_opportunity, props.client?.client?.account, props.client?.net_worth, props.client?.produto_a_vencer, isOpportunity, rankings, setLoading, setResult, setError])

    return (
        <Accordion.Summary
            open={props.open}
            onToggle={props.onToggle}
            toggleDisabled={CLIENT_EVENT_ACCORDION_EXPAND_DISABLED}
        >
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
                <Grid container wrap="nowrap" alignItems="flex-start" gap={2} sx={{ minWidth: 0 }}>
                    <Checkbox
                        color="secondary"
                        checked={checked}
                        disabled={isDemo}
                        onClick={handleCheckboxClick}
                    />

                    <Grid sx={{ flexGrow: 0, flexShrink: 1, minWidth: 0 }}>
                        {/* Cliente protagonista + conta + perfil */}
                        <Grid container alignItems="center" gap={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {props.client.client.name}
                            </Typography>
                            <InvestorProfileChip profile={mapPerfilToProfile(props.client.client.perfil)} />
                            {isOpenFinance && (
                                <Chip
                                    size="small"
                                    label={intl("openFinance")}
                                    sx={{ backgroundColor: "#0D9488", color: "#fff", fontWeight: 600 }}
                                />
                            )}
                        </Grid>
                        {props.client.client.account && (
                            <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.5 }}>
                                {intl("accountLabel")}: {props.client.client.account}
                            </Typography>
                        )}

                        {/* Urgência (abaixo do cliente) */}
                        {urgency && (
                            <Grid container alignItems="center" gap={0.75} sx={{ mb: 1, width: "auto" }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: urgency.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="caption" sx={{ color: urgency.color, fontWeight: 700 }}>
                                    {urgency.label}
                                </Typography>
                            </Grid>
                        )}

                        <Grid container direction="column" gap={1.25}>
                            <Grid container gap={3} alignItems="flex-start">
                                <Grid sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.5 }}>
                                        {intl("liquidityStream")}
                                    </Typography>
                                    {streamChip}
                                </Grid>
                                <MetaItem
                                    label={intl("financialInstitution")}
                                    value={institutionLabel || "BTG"}
                                />
                                <MetaItem
                                    label={intl("eventLabel")}
                                    value={
                                        isOpportunity
                                            ? `${opportunities.length} ${intl("opportunities").toLowerCase()}`
                                            : eventSubtitle
                                                ? `${eventTitle} · ${eventSubtitle}`
                                                : eventTitle
                                    }
                                />
                            </Grid>
                            <Grid container gap={3} alignItems="flex-start">
                                {isOpportunity ? (
                                    <MetaItem
                                        label={intl("totalAmount")}
                                        value={formatCurrencyBRL(totalOpportunityValue)}
                                        emphasize
                                    />
                                ) : (
                                    <>
                                        <MetaItem
                                            label={intl("financialImpact")}
                                            value={formatCurrencyBRL(props.client.net_amount)}
                                            emphasize
                                        />
                                        <MetaItem
                                            label={intl("equity")}
                                            value={formatCurrencyBRL(props.client.net_worth)}
                                        />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <AllocationStrategyBars
                    allocationDetails={props.client?.allocation_details}
                    perfil={props.client?.client?.perfil}
                    simulatedInvestment={simulatedInvestment}
                />
            </Box>
        </Accordion.Summary>
    )
}

export { Summary }
